/**
 * What this package needs from a notation renderer, and nothing more.
 *
 * Drawing engraved notation is a large, slow-moving problem that has already been solved
 * well -- Verovio does it -- and solving it again here would be years of work for a worse
 * result. What the editor cannot borrow is everything above the drawing: selection, input,
 * undo. So the renderer is held at arm's length, as the structural interface below.
 *
 * Nothing in this package imports an engine. A caller constructs one and passes it in, so
 * the package keeps no runtime dependencies, the engine can be swapped for another that
 * answers the same questions, and the document layer stays usable on a server where no
 * renderer exists at all.
 *
 * The interface is deliberately the smallest set of calls that supports an editor: give it
 * a document, get pages of SVG back, and ask which drawn element is sounding at a moment.
 * It is written as a structural type rather than a class so that an engine already shaped
 * this way satisfies it without an adapter.
 */

import type { Layout } from "../document/layout";

/** What an engine reports as sounding at one instant, used for playback highlighting. */
export interface ElementsAtTime {
  readonly page: number;
  readonly notes: readonly string[];
  readonly rests: readonly string[];
  readonly chords: readonly string[];
  readonly measure: string;
}

/**
 * A notation engine, as this package uses it.
 *
 * The one thing an engine must do beyond drawing is **keep the `id` attributes it was
 * given**: the editor stamps every note and bar with the document's own id on the way in
 * and finds them again on the drawn output. An engine that renames elements can still be
 * used to look at a score, but not to edit one, because a click would land on nothing.
 */
export interface NotationEngine {
  setOptions(options: Record<string, unknown>): void;
  /** Load a MusicXML document. `false` means the engine rejected it. */
  loadData(data: string): boolean;
  getPageCount(): number;
  /** SVG for one page, 1-based, as the engine's own markup. */
  renderToSVG(page: number): string;
  getElementsAtTime(milliseconds: number): ElementsAtTime;
  /** Milliseconds from the start of the piece at which a drawn element sounds. */
  getTimeForElement(id: string): number;
  /**
   * The loaded document as MEI, for an engine that keeps one.
   *
   * Optional, and asked for one thing only: condensing a score needs a flag that only MEI
   * states, so a document that wants its silent staves left out is handed back and loaded
   * again with the flag in it. See `render/condense.ts`. An engine without this draws
   * every staff, which is what an editor wants anyway.
   */
  getMEI?(options?: Record<string, unknown>): string;
}

/** How a score should be laid out. Sizes are in the engine's own units, not pixels. */
export interface RenderOptions {
  /** Percent. 100 is the engine's natural size; 40 fits an A4 page on a laptop screen. */
  readonly scale?: number;
  /** Page width in tenths of a millimetre at scale 100. A4 is 2100 x 2970. */
  readonly pageWidth?: number;
  readonly pageHeight?: number;
  /**
   * Shrink the page to the music instead of drawing the whole sheet.
   *
   * On by default: an editor scrolls a continuous document rather than turning paper, and
   * a half-empty last page is blank space the user has to scroll past.
   */
  readonly adjustPageHeight?: boolean;
  /**
   * Where systems may break. `auto` lets the engine decide, `encoded` obeys the breaks the
   * document asks for and no others, `smart` prefers them but may add its own.
   *
   * A caller rarely states this: {@link renderScore} works it out from the breaks the
   * score encodes, and a break the user asked for is worth nothing if the engine is left
   * to decide. See `render/breaks.ts`.
   */
  readonly breaks?: "auto" | "none" | "encoded" | "smart";
  /** Extra space between staves, and between systems, in the engine's units. */
  readonly spacingStaff?: number;
  readonly spacingSystem?: number;
  /** The height of a five-line staff, in millimetres. */
  readonly staffHeight?: number;
  /**
   * Whether a staff whose part is silent is left out of a system.
   *
   * `none` draws every staff, which is what an editor wants. `auto` lets the engine drop
   * the silent ones, which is what a conductor's score wants. `encoded` drops only the
   * ones the document asked to drop.
   */
  readonly condense?: "none" | "auto" | "encoded";
  /**
   * Print a bar number every so many bars.
   *
   * Left unstated, the engine numbers the first bar of each system, which is what a
   * printed score does. Numbering none of them is not here at all: it is a line in the
   * score the engine is given, because the score is where a printer looks for it.
   */
  readonly barNumberInterval?: number;
  /** The border round the music, in tenths of a millimetre, as the page sizes are. */
  readonly marginLeft?: number;
  readonly marginRight?: number;
  readonly marginTop?: number;
  readonly marginBottom?: number;
}

/** Tenths of a millimetre, which is the unit every page measurement below is in. */
function tenthsOfMillimetre(millimetres: number): number {
  return Math.round(millimetres * 10);
}

/** The engine's own unit when nothing states a staff height, in its own units. */
const DEFAULT_UNIT = 9;

/**
 * The units the engine accepts, read off Verovio 6.3.0's `getAvailableOptions()`.
 *
 * One outside it is refused with an error and ignored -- the engine keeps whatever unit it
 * had, its default of 9 when fresh -- so a staff the engine is told about has to fall
 * inside it. That is a staff of 3.6 to 9.6mm, which holds every size the editor lets a user
 * choose; what falls outside is an imported file stating its own -- an orchestral score
 * written at 2.8mm, say.
 */
const UNIT_RANGE = { min: 4.5, max: 12 } as const;

/** The engine's unit is half the space between two staff lines: eight to a staff. */
const UNITS_PER_MILLIMETRE_OF_STAFF = 10 / 8;

/** A staff height as the engine can draw it: the nearest one inside its range. */
function drawnStaffHeight(staffHeight: number): number {
  const least = UNIT_RANGE.min / UNITS_PER_MILLIMETRE_OF_STAFF;
  const most = UNIT_RANGE.max / UNITS_PER_MILLIMETRE_OF_STAFF;
  return Math.min(most, Math.max(least, staffHeight));
}

/**
 * The document's own page, as options for the engine.
 *
 * Two conversions happen here and nowhere else. A page is measured in millimetres in the
 * document and in tenths of a millimetre by the engine, which is a factor of ten. The
 * distances *between* staves are measured by the engine in its own unit -- half the space
 * between two staff lines, so eight of them to a staff -- because a gap that did not grow
 * with the music would swallow the page as soon as the staves grew.
 *
 * The staff is given as the engine can draw it: a height outside its range is drawn at the
 * nearest one inside, and the document keeps the height it stated. The gaps are still
 * counted against the stated height, so a staff drawn larger than its file says keeps the
 * space round it in the proportion the file set.
 */
export function layoutOptions(layout: Layout): RenderOptions {
  const units = (millimetres: number): number => Math.round((millimetres * 8) / layout.staffHeight);
  return {
    staffHeight: drawnStaffHeight(layout.staffHeight),
    pageWidth: tenthsOfMillimetre(layout.pageWidth),
    pageHeight: tenthsOfMillimetre(layout.pageHeight),
    marginLeft: tenthsOfMillimetre(layout.margins.left),
    marginRight: tenthsOfMillimetre(layout.margins.right),
    marginTop: tenthsOfMillimetre(layout.margins.top),
    marginBottom: tenthsOfMillimetre(layout.margins.bottom),
    spacingStaff: units(layout.staffGap),
    spacingSystem: units(layout.systemGap),
    condense: layout.hideEmptyStaves ? "auto" : "none",
    // Only a count is an option. The other two settings are numbering the way the engine
    // does already, and not numbering at all, which the exported score states itself.
    ...(typeof layout.barNumbers === "number" ? { barNumberInterval: layout.barNumbers } : {}),
  };
}

/**
 * The engine's unit for a set of options: half the space between two staff lines.
 *
 * Everything the engine draws is measured in it, so anything laid over the drawn page --
 * a mark moved by hand, a box drawn round a bar -- has to be converted through it rather
 * than through pixels, which change with the zoom while this does not. It is the unit the
 * engine actually draws at, held to the range it accepts, so an overlay and the page it
 * lies on are always at one scale.
 */
export function unitOf(options: RenderOptions): number {
  return options.staffHeight === undefined
    ? DEFAULT_UNIT
    : drawnStaffHeight(options.staffHeight) * UNITS_PER_MILLIMETRE_OF_STAFF;
}

const DEFAULTS = {
  scale: 40,
  pageWidth: 2100,
  pageHeight: 2970,
  adjustPageHeight: true,
  breaks: "auto",
  condense: "none",
} as const;

/** An option the engine is only told about when the caller states it. */
function stated(name: string, value: number | undefined): Record<string, number> {
  return value === undefined ? {} : { [name]: value };
}

/**
 * Turn render options into the flat record an engine's `setOptions` wants.
 *
 * The header and footer are fixed rather than offered, and always off. They are the
 * engraver's title block, drawn from the file's metadata, and in an editor the score's
 * title is part of the surrounding interface where it can be clicked and changed --
 * drawing it twice would be confusing and only one of the two would be editable.
 *
 * Staff condensing is off unless it is asked for, and the layout is what asks
 * (`Layout.hideEmptyStaves`). An engine left to itself hides a staff on a system where its
 * part is silent, which is right for a printed conductor's score and wrong for an editor:
 * the rests that were hidden are exactly the places the user clicks to start writing, and
 * a part that disappears when it falls silent cannot be selected in order to stop being
 * silent -- a hidden staff has no drawn events, so `render/slots.ts` finds no moment there
 * to put the caret on. That is why it is off in a new score and why the host says what it
 * costs; it is a decision about the page, not a default.
 */
export function engineOptions(options: RenderOptions = {}): Record<string, unknown> {
  const merged = { ...DEFAULTS, ...options };
  return {
    scale: merged.scale,
    pageWidth: merged.pageWidth,
    pageHeight: merged.pageHeight,
    adjustPageHeight: merged.adjustPageHeight,
    breaks: merged.breaks,
    // An engine preferring the document's breaks still drops one whose system would come
    // out under two thirds full, which is exactly the short system a user asks for by
    // hand. At zero it keeps every one of them and still chooses the rest itself.
    ...(merged.breaks === "smart" ? { breaksSmartSb: 0 } : {}),
    condense: merged.condense,
    // A condensed score that drew its first page in full would be two different scores in
    // one document, and in an editor the first page is the one being worked on.
    condenseFirstPage: merged.condense !== "none",
    footer: "none",
    header: "none",
    ...stated("spacingStaff", options.spacingStaff),
    ...stated("spacingSystem", options.spacingSystem),
    ...stated("unit", options.staffHeight === undefined ? undefined : unitOf(options)),
    ...stated("mnumInterval", options.barNumberInterval),
    ...stated("pageMarginLeft", options.marginLeft),
    ...stated("pageMarginRight", options.marginRight),
    ...stated("pageMarginTop", options.marginTop),
    ...stated("pageMarginBottom", options.marginBottom),
  };
}
